// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessMyVouchers.jsx
// Название: Business My Vouchers - DARY
// Версия: 1.0.0
// Дата: 03.12.2025

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import VoucherPreview from '../User/VoucherPreview';
import VoucherCard from '../User/VoucherCard';
import './BusinessVoucher.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const BusinessMyVouchers = () => {
  const [activeTab, setActiveTab] = useState('my');
  const [vouchers, setVouchers] = useState([]);
  const [filteredVouchers, setFilteredVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [archiveCount, setArchiveCount] = useState(0);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  
  // Фильтры
  const [filterAmount, setFilterAmount] = useState('all');
  const [filterTheme, setFilterTheme] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetchVouchers();
    fetchStats();
  }, [activeTab]);

  useEffect(() => {
    applyFilters();
  }, [vouchers, filterAmount, filterTheme, sortBy]);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      let data = [];
      
      if (activeTab === 'my') {
        const res = await axios.get(API_URL + '/api/vouchers/my', {
          headers: { Authorization: 'Bearer ' + token }
        });
        if (res.data.success) {
          data = (res.data.data || []).filter(v => v.status === 'active');
        }
      } else if (activeTab === 'received') {
        const res = await axios.get(API_URL + '/api/vouchers/received', {
          headers: { Authorization: 'Bearer ' + token }
        });
        if (res.data.success) {
          data = res.data.data || [];
        }
      } else if (activeTab === 'archive') {
        const res = await axios.get(API_URL + '/api/vouchers/my', {
          headers: { Authorization: 'Bearer ' + token }
        });
        if (res.data.success) {
          data = (res.data.data || []).filter(v => 
            v.status === 'used' || v.status === 'expired' || v.status === 'cancelled'
          );
          setArchiveCount(data.length);
        }
      }
      
      setVouchers(data);
    } catch (err) {
      setError('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(API_URL + '/api/vouchers/stats', {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.data.success) {
        setStats(res.data.data);
      }
      
      const allRes = await axios.get(API_URL + '/api/vouchers/my', {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (allRes.data.success) {
        const archived = (allRes.data.data || []).filter(v => 
          v.status === 'used' || v.status === 'expired' || v.status === 'cancelled'
        );
        setArchiveCount(archived.length);
      }
    } catch (err) {}
  };

  const applyFilters = () => {
    let result = [...vouchers];
    
    // Фильтр по сумме
    if (filterAmount !== 'all') {
      const [min, max] = filterAmount.split('-').map(Number);
      result = result.filter(v => {
        const amt = parseFloat(v.amount);
        if (max) return amt >= min && amt <= max;
        return amt >= min;
      });
    }
    
    // Фильтр по теме
    if (filterTheme !== 'all') {
      result = result.filter(v => v.theme_name === filterTheme);
    }
    
    // Сортировка
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'amount_desc':
          return parseFloat(b.amount) - parseFloat(a.amount);
        case 'amount_asc':
          return parseFloat(a.amount) - parseFloat(b.amount);
        default: // date_desc
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    
    setFilteredVouchers(result);
  };

  const handleCancel = async (voucherId, e) => {
    e.stopPropagation();
    if (!window.confirm('Отменить подарок? Средства вернутся на баланс.')) return;
    
    try {
      const res = await axios.post(API_URL + '/api/vouchers/' + voucherId + '/cancel', {}, {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.data.success) {
        fetchVouchers();
        fetchStats();
        setSelectedVoucher(null);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка');
    }
  };

  const handleDelete = async (voucherId, e) => {
    e.stopPropagation();
    if (!window.confirm('Удалить подарок из истории?')) return;
    
    try {
      const res = await axios.delete(API_URL + '/api/vouchers/' + voucherId, {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.data.success) {
        fetchVouchers();
        fetchStats();
        setSelectedVoucher(null);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка удаления');
    }
  };

  const handleClearArchive = async () => {
    if (!window.confirm('Удалить все подарки из архива? Это действие необратимо.')) return;
    
    try {
      const res = await axios.delete(API_URL + '/api/vouchers/archive/clear', {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (res.data.success) {
        fetchVouchers();
        fetchStats();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка очистки');
    }
  };

  const exportCSV = () => {
    if (filteredVouchers.length === 0) {
      alert('Нет данных для экспорта');
      return;
    }
    
    const headers = ['ID', 'Сумма', 'Статус', 'Тема', 'Код', 'Создан', 'Истекает'];
    const rows = filteredVouchers.map(v => [
      v.id,
      v.amount,
      v.status,
      v.theme_name || 'default',
      v.fallback_code,
      formatDate(v.created_at),
      v.ttl ? formatDate(v.ttl) : ''
    ]);
    
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'vouchers_' + new Date().toISOString().split('T')[0] + '.csv';
    link.click();
  };

  const copyCode = (code, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(code);
  };

  const copyLink = (code, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText('https://dary.one/vouchers/claim?code=' + code);
  };

  const getStatusText = (status) => {
    const map = { active: 'Активен', used: 'Использован', expired: 'Истёк', cancelled: 'Отменён' };
    return map[status] || status;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const getTimeLeft = (ttl) => {
    if (!ttl) return null;
    const diff = new Date(ttl) - new Date();
    if (diff <= 0) return 'Истёк';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hours > 24) return Math.floor(hours / 24) + ' дн.';
    if (hours > 0) return hours + ' ч. ' + mins + ' мин.';
    return mins + ' мин.';
  };

  const formatAmount = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  // Уникальные темы для фильтра
  const uniqueThemes = [...new Set(vouchers.map(v => v.theme_name).filter(Boolean))];

  return (
    <div className="biz-my-vouchers-container">
      <div className="biz-vouchers-header">
        <h2>🎁 Мои подарки</h2>
        <button className="biz-btn-export" onClick={exportCSV}>
          📥 Экспорт CSV
        </button>
      </div>

      {stats && (
        <div className="biz-stats-row">
          <div className="biz-stat-item">
            <span className="biz-stat-value">{stats.active_count || 0}</span>
            <span className="biz-stat-label">активных</span>
          </div>
          <div className="biz-stat-item highlight">
            <span className="biz-stat-value">{formatAmount(stats.active_amount || 0)}</span>
            <span className="biz-stat-label">DAR в подарках</span>
          </div>
          <div className="biz-stat-item">
            <span className="biz-stat-value">{stats.total_created || 0}</span>
            <span className="biz-stat-label">создано всего</span>
          </div>
          <div className="biz-stat-item">
            <span className="biz-stat-value">{stats.total_claimed || 0}</span>
            <span className="biz-stat-label">получено</span>
          </div>
        </div>
      )}

      <div className="biz-tabs">
        <button className={'biz-tab ' + (activeTab === 'my' ? 'active' : '')} onClick={() => setActiveTab('my')}>
          📤 Мои <span className="biz-tab-count">{stats?.active_count || 0}</span>
        </button>
        <button className={'biz-tab ' + (activeTab === 'received' ? 'active' : '')} onClick={() => setActiveTab('received')}>
          📥 Полученные
        </button>
        <button className={'biz-tab ' + (activeTab === 'archive' ? 'active' : '')} onClick={() => setActiveTab('archive')}>
          📁 Архив <span className="biz-tab-count">{archiveCount}</span>
        </button>
      </div>

      {/* Фильтры */}
      {vouchers.length > 0 && (
        <div className="biz-filters-row">
          <div className="biz-filter">
            <label>Сумма:</label>
            <select value={filterAmount} onChange={(e) => setFilterAmount(e.target.value)}>
              <option value="all">Все</option>
              <option value="0-25">до 25 DAR</option>
              <option value="25-100">25-100 DAR</option>
              <option value="100-500">100-500 DAR</option>
              <option value="500-10000">500+ DAR</option>
            </select>
          </div>
          <div className="biz-filter">
            <label>Тема:</label>
            <select value={filterTheme} onChange={(e) => setFilterTheme(e.target.value)}>
              <option value="all">Все</option>
              {uniqueThemes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="biz-filter">
            <label>Сортировка:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="date_desc">Сначала новые</option>
              <option value="date_asc">Сначала старые</option>
              <option value="amount_desc">По сумме ↓</option>
              <option value="amount_asc">По сумме ↑</option>
            </select>
          </div>
        </div>
      )}

      {activeTab === 'archive' && archiveCount > 0 && (
        <div className="biz-archive-actions">
          <button className="biz-btn-clear-archive" onClick={handleClearArchive}>
            🗑️ Очистить архив
          </button>
        </div>
      )}

      {error && <div className="biz-error-message">{error}</div>}

      {loading ? (
        <div className="biz-loading">Загрузка...</div>
      ) : filteredVouchers.length === 0 ? (
        <div className="biz-empty-state">
          <div className="biz-empty-icon">{activeTab === 'archive' ? '📁' : '🎁'}</div>
          <p>{activeTab === 'my' ? 'Нет активных подарков' : activeTab === 'received' ? 'Нет полученных' : 'Архив пуст'}</p>
        </div>
      ) : (
        <div className="biz-vouchers-grid">
          {filteredVouchers.map(v => (
            <div key={v.id} className={'biz-voucher-item status-' + v.status} onClick={() => setSelectedVoucher(v)}>
              <VoucherPreview 
                amount={v.amount} 
                code={v.fallback_code} 
                templateTitle={v.theme_name}
                size={150}
              />
              <div className="biz-voucher-info">
                <span className="biz-voucher-amount">{formatAmount(v.amount)} DAR</span>
                <span className={'biz-voucher-status status-' + v.status}>{getStatusText(v.status)}</span>
                {v.status === 'active' && v.ttl && (
                  <span className="biz-voucher-ttl">⏱️ {getTimeLeft(v.ttl)}</span>
                )}
              </div>
              {v.status === 'active' && activeTab === 'my' && (
                <div className="biz-voucher-quick-actions">
                  <button onClick={(e) => copyCode(v.fallback_code, e)} title="Код">📋</button>
                  <button onClick={(e) => copyLink(v.fallback_code, e)} title="Ссылка">🔗</button>
                  <button className="btn-x" onClick={(e) => handleCancel(v.id, e)} title="Отменить">✕</button>
                </div>
              )}
              {(v.status === 'used' || v.status === 'expired' || v.status === 'cancelled') && (
                <div className="biz-voucher-quick-actions">
                  <button className="btn-delete" onClick={(e) => handleDelete(v.id, e)} title="Удалить">🗑️</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {selectedVoucher && (
        <div className="biz-modal-overlay" onClick={() => setSelectedVoucher(null)}>
          <div className="biz-modal" onClick={(e) => e.stopPropagation()}>
            <button className="biz-modal-close" onClick={() => setSelectedVoucher(null)}>✕</button>
            <VoucherCard 
              amount={selectedVoucher.amount}
              code={selectedVoucher.fallback_code}
              templateTitle={selectedVoucher.theme_name}
              voucherToken={selectedVoucher.voucher_token}
            />
            <div className="biz-modal-info">
              <p><strong>Сумма:</strong> {formatAmount(selectedVoucher.amount)} DAR</p>
              <p><strong>Статус:</strong> {getStatusText(selectedVoucher.status)}</p>
              <p><strong>Создан:</strong> {formatDate(selectedVoucher.created_at)}</p>
              {selectedVoucher.ttl && selectedVoucher.status === 'active' && (
                <p><strong>Осталось:</strong> {getTimeLeft(selectedVoucher.ttl)}</p>
              )}
              {selectedVoucher.used_at && <p><strong>Использован:</strong> {formatDate(selectedVoucher.used_at)}</p>}
            </div>
            {selectedVoucher.status === 'active' && activeTab === 'my' && (
              <button className="biz-btn-cancel-modal" onClick={(e) => handleCancel(selectedVoucher.id, e)}>
                Отменить подарок
              </button>
            )}
            {(selectedVoucher.status === 'used' || selectedVoucher.status === 'expired' || selectedVoucher.status === 'cancelled') && (
              <button className="biz-btn-delete-modal" onClick={(e) => handleDelete(selectedVoucher.id, e)}>
                🗑️ Удалить из истории
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessMyVouchers;
